/**
 * Abstract base class for bidirectional data transformers.
 *
 * Enforces a type-safe contract for mapping between a GraphQL source
 * type and a UI form schema. Concrete implementations enable polymorphic
 * component reuse across different data sources (CustomsDeclaration,
 * Shipment, future entry types).
 *
 * @see LLD Section 3.2 — Transform Classes (Recommended)
 * https://quip-amazon.com/pCvnA3wHOUu6#eFZ9CA34zg8
 *
 * @typeParam TSource - The GraphQL/API query data shape
 * @typeParam TUISchema - The form state shape consumed by React Hook Form
 * @typeParam TOutput - The mutation input shape (defaults to TSource for symmetric transformers)
 */
export abstract class BaseTransformer<TSource, TUISchema, TOutput = TSource> {
  /**
   * Transform API/GraphQL data into the form state shape.
   * Called when loading data into React Hook Form's defaultValues.
   */
  abstract toForm(source: TSource): TUISchema;

  /**
   * Transform form state back into the mutation input shape.
   * Receives the original source to preserve fields not represented in the form.
   */
  abstract toSchema(formData: TUISchema, original: TSource): TOutput;
}
